import { FC, useEffect, useState } from "react";
import "./ModalPlansInsert.scss";
import ModalDefault from "../../../ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Col, FormGroup, Row } from "react-bootstrap";
import ErrorMessage from "../../../ErrorMessage/ErrorMessage";
import { schema } from "./Validate";
import ReactSelect from "react-select";
import { customStyles } from "../../../../models/SelectCustomStyles";
import { typePlans } from "../../../../models/Financial/OptionsValues";
import { usePlans } from "../../../../providers/Financial/Plans/PlansProvider";
import { PlansModel } from "../../../../models/Financial/Plans/PlansModel";

interface ModalPlansInsertProps {
  show: boolean;
  planEdit?: PlansModel;
  onClose: () => void;
}

const ModalPlansInsert: FC<ModalPlansInsertProps> = ({
  show,
  onClose,
  planEdit,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [selectedTypePlans, setSelectedTypePlans] = useState<any | null>(null);
  const [isArticleIA, setIsArticleIA] = useState<boolean>(false);

  const { onInvalid } = useAuth();
  const { handleFetch, setError, handleList } = usePlans();

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const clear = () => {
    reset({
      name: "",
      description: "",
      plan_type: "",
      max_users: 0,
      message_limit: null,
    });
    setError("");
    setValue("name", "");
    setValue("description", "");
    setValue("plan_type", "");
    setValue("max_users", 0);
    setValue("message_limit", null);
    setSelectedTypePlans(null);
    setIsArticleIA(false);
  };

  const handleClose = () => {
    clear();
    onClose();
  };

  const onSubmit = async (data: any) => {
    setIsLoadingSubmit(true);
    setError("");
    const ret = await handleFetch(data as any);
    if (ret) {
      toast.success("Plano salvo com sucesso!");
      handleList();
      handleClose();
    }
    setIsLoadingSubmit(false);
  };

  useEffect(() => {
    if (show) {
      if (planEdit) {
        const article = typePlans.filter(
          (x: any) => x.value === planEdit?.plan_type,
        );
        setIsArticleIA(article?.[0]?.value === "artigos_ia" ? true : false);
        setValue("id", planEdit?.id);
        setValue("name", planEdit?.name ?? "");
        setValue("description", planEdit?.description ?? "");
        setValue("plan_type", planEdit?.plan_type ?? "");
        setValue("max_users", planEdit?.max_users ?? 0);
        setValue("message_limit", planEdit?.message_limit ?? null);
        setSelectedTypePlans(
          typePlans.filter((x: any) => x.value === planEdit?.plan_type),
        );
      } else {
        clear();
      }
    } else {
      handleClose();
    }
  }, [show]);

  useEffect(() => {
    if (!isArticleIA) {
      setValue("message_limit", null);
    }
    setValue("isArticleIA", isArticleIA);
  }, [isArticleIA]);

  return (
    <ModalDefault
      title={planEdit?.id ? "Editar Planos" : "Adicionar Planos"}
      show={show}
      onClose={handleClose}
      sizeModal={"lg"}
      showFooter={true}
      buttonText={isLoadingSubmit ? "Salvando..." : "Salvar"}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div className="ModalPlansInsert" data-testid="ModalPlansInsert">
        <Row>
          <Col md={12}>
            <FormGroup className="form-group">
              <label htmlFor="name">NOME DO PLANO *</label>
              <input
                className={`form-control ${onInvalid(errors?.name)}`}
                type="text"
                {...register("name")}
                onChange={(e: any) => setValue("name", e?.target?.value)}
              />
              <ErrorMessage
                name={"Nome"}
                type={errors?.name?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup className="form-group">
              <label htmlFor="description">DESCRIÇÃO *</label>
              <textarea
                {...register("description")}
                className={`form-control ${onInvalid(errors?.description)}`}
                rows={3}
                onChange={(e: any) => setValue("description", e?.target?.value)}
              ></textarea>
              <ErrorMessage
                name={"Descrição"}
                type={errors?.description?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
          <Col md={12} lg={6}>
            <FormGroup className="form-group">
              <label htmlFor="plan_type">TIPO DO PLANO *</label>
              <ReactSelect
                isSearchable
                options={typePlans}
                value={selectedTypePlans ?? ""}
                defaultValue={selectedTypePlans}
                placeholder="Selecione..."
                className={`form-control p-0 ${onInvalid(errors?.plan_type)}`}
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(val: any) => {
                  setValue("plan_type", val?.value);
                  setSelectedTypePlans(val);
                  setIsArticleIA(val?.value === "artigos_ia" ? true : false);
                }}
              />
              <ErrorMessage
                name={"Tipo do plano"}
                type={errors?.plan_type?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
          <Col md={12} lg={6}>
            <FormGroup className="form-group">
              <label htmlFor="max_users">QUANTIDADE MÁXIMA DE USUÁRIOS *</label>
              <input
                className={`form-control ${onInvalid(errors?.max_users)}`}
                type="number"
                {...register("max_users")}
                onChange={(e: any) => setValue("max_users", e?.target?.value)}
              />
              <ErrorMessage
                name={"Quantidade máxima de usuários"}
                type={errors?.max_users?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
          {isArticleIA && (
            <Col md={12} lg={6}>
              <FormGroup className="form-group">
                <label htmlFor="message_limit">
                  LIMITE DE MENSAGENS POR MÊS *
                </label>
                <input
                  className={`form-control ${onInvalid(errors?.message_limit)}`}
                  type="number"
                  {...register("message_limit")}
                  onChange={(e: any) =>
                    setValue("message_limit", e?.target?.value)
                  }
                />
                <ErrorMessage
                  name={"Limite de mensagens por mês"}
                  type={errors?.message_limit?.type?.toString()}
                  min={3}
                  max={100}
                />
              </FormGroup>
            </Col>
          )}
        </Row>
      </div>
    </ModalDefault>
  );
};

export default ModalPlansInsert;
